import axios, { AxiosError, AxiosResponse } from 'axios';
import { saveTokenResource, loadTokenResource, clearAuthCookie } from 'helpers/auth';
import { setUserInitials, signOut as signOutCreator } from 'reducers/auth';
import { store } from './index';
import { BASE_URL, endpoints } from 'endpoints.config';
import { createUseTable } from '@avamae/table';
import { createUseFetch } from '@avamae/use-fetch';
import { addSeconds } from 'date-fns';
import Cookies from 'universal-cookie';
import { DOMAIN } from 'endpoints.config';
import { navigate } from '@reach/router';
import { PUBLIC_SITE_URL, SESSION_EXPIRED_MESSAGE } from 'appConstants';
import { holdForMfa, isMfaRequiredError, replayHeldRequests } from 'helpers/mfa/mfaGate';

const signOut = () => signOutCreator(SESSION_EXPIRED_MESSAGE);

const { refreshAccessToken: refreshEndpoint } = endpoints.auth;

/**
 * Custom axios instance with base url set, and some interceptors to
 * handle auth headers.
 *
 */
const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // AB#210768 - send the HttpOnly auth cookies with each request
});

// AB#210768 - the SPA echoes the CSRF cookie back in a header (double-submit) for the API's check.
const getCsrfToken = (): string | undefined => {
  try {
    return new Cookies().get('csrf_token');
  } catch {
    return undefined;
  }
};

/**
 * A function that, given an access token, will modify an axios request
 * config and re-run the request.
 */
type RequestCallback = (token: string) => void;

/**
 * Some mutable state for a queue of requests. If we're already in the middle of
 * requesting a new token, we want to hold other requests in a queue, then make
 * them with the new token once we get it.
 */
class RequestHandler {
  isFetchingToken: boolean;
  private pendingRequests: RequestCallback[];

  constructor() {
    this.isFetchingToken = false;
    this.pendingRequests = [];
  }

  addToQueue = (callback: RequestCallback) => {
    this.pendingRequests.push(callback);
  };

  onTokenFetched = (token: string) => {
    this.pendingRequests.forEach((cb) => cb(token));
    this.clearQueue();
  };

  clearQueue = () => {
    this.pendingRequests = [];
    this.isFetchingToken = false;
  };
}

const reqHandler = new RequestHandler();

// AB#210768 - the access token travels in an HttpOnly cookie (sent via withCredentials), so we no
// longer attach an Authorization header. We do attach the CSRF header (double-submit) and keep
// dispatching the cached profile initials.
instance.interceptors.request.use((request) => {
  try {
    const resource = loadTokenResource();
    resource?.profileInitials &&
      store.dispatch(setUserInitials(resource?.profileInitials));
    const csrf = getCsrfToken();
    return {
      ...request,
      withCredentials: true,
      headers: {
        ...request.headers,
        ...(csrf ? { 'X-CSRF-TOKEN': csrf } : {}),
      },
    };
  } catch (err) {
    return request;
  }
});

const isMfaChallengeRequest = (rejected: AxiosError) =>
  (rejected.config?.url ?? '').includes('/schoolrole/profilemodule/');

// After each request, if it comes back with a 401 error, refresh token and then retry.
instance.interceptors.response.use(
  (fulfilled) => {
    return fulfilled;
  },
  (rejected: AxiosError) => {
    const status = rejected.response?.status;

    if (status === 401 /*&& has the expired token header*/) {
      // Refresh access token.
      return refreshAccessToken(rejected);
    }

    // The challenge screen's own calls must never be held, or the gate would wait on itself.
    if (isMfaRequiredError(rejected) && !isMfaChallengeRequest(rejected)) {
      return holdForMfa(rejected);
    }

    // It's not an authorisation issue, just pass on the rejection.
    // store.dispatch(signOut());
    return Promise.reject(rejected);
  }
);

/**
 * Intercept the error, and replace it with a promise (awaitingNewToken), which
 * on construction will add a callback to our RequestHandler's queue.
 *
 * The first time the request handler receives one of these additions to its
 * queue, it will attempt to fetch a new access token. It stores up pending
 * requests until the new token arrives. To the initial callers it looks like
 * their request is waiting for a response from the server.
 *
 * Once the request handler has fetched a new access token, it will run every
 * callback in its queue. The callback takes an original request's config,
 * changes it to use the new access token, and remakes the request. It then
 * resolves the promise made in awaitingNewToken. The response to this new
 * request is thereby delivered to the original caller. The queue is then cleared.
 *
 * To that initial caller, it just looks like its original request took a while
 * to resolve.
 */
const refreshAccessToken = async (rejected: AxiosError) => {
  type RefreshResponse = {
    status: string;
    accessToken: string;
    tokenType: string;
    refreshTokenExpiresIn: string;
    expiresIn: string;
    refreshToken: string;
    profileInitials: string;
  };

  try {
    const { response: errorResponse } = rejected;

    // Extract the refresh token from LS and make sure it's there.
    // Otherwise, just return the error.
    const resource = loadTokenResource();
    resource?.profileInitials &&
      store.dispatch(setUserInitials(resource?.profileInitials));

    if (resource == null) {
      store.dispatch(signOut());
      localStorage.setItem('SP', 'synced-sign-out');
      return Promise.reject(rejected);
    }

    // Build a promise that will be handed to the caller instead of the error.
    // AB#210768 - retry with the cookie (withCredentials); no Authorization header.
    const awaitingNewToken = new Promise((resolve) => {
      reqHandler.addToQueue(() => {
        if (errorResponse) {
          resolve(axios({ ...errorResponse.config, withCredentials: true }));
        }
      });
    });

    // Check if we're already trying to fetch a replacement access token.
    if (!reqHandler.isFetchingToken) {
      reqHandler.isFetchingToken = true;

      // AB#210768 - refresh is driven by the HttpOnly cookies; send an empty body with credentials.
      const response = await axios.post<
        {},
        AxiosResponse<{ details: any, errors: any } & RefreshResponse>
      >(refreshEndpoint, {}, { withCredentials: true });

      if (
        !response.data ||
        (response.data.errors && response.data.errors.length > 0)
      ) {
        // The refresh request failed, reset the queue, sign the
        // user out and return the error.
        clearAuthCookie();
        reqHandler.clearQueue();
        store.dispatch(signOut());
        localStorage.setItem('SP', 'synced-sign-out');
        return Promise.reject(rejected);
      }

      // The refresh request succeeded, save the token details for future
      // requests and make the queued requests again.
      const {
        accessToken,
        refreshToken,
        expiresIn,
        refreshTokenExpiresIn,
        profileInitials,
      } = response.data.details;
      const tokenExpires = addSeconds(new Date(), parseInt(expiresIn, 10));
      const refreshTokenExpires = addSeconds(
        new Date(),
        parseInt(refreshTokenExpiresIn, 10)
      );
      const cookies = new Cookies();

      cookies.set('GETSET_SchoolUser', 'sign-in', {
        expires: tokenExpires,
        domain: DOMAIN,
        sameSite: "none",
        secure: true
      });

      saveTokenResource({
        tokenExpires,
        accessToken,
        refreshToken,
        refreshTokenExpires,
        profileInitials,
      });
      reqHandler.onTokenFetched(accessToken);
    }

    // Return the promise, which will fulfill when we have a new token,
    // instead of the error.
    return awaitingNewToken;
  } catch (error) {
    // Something went generically wrong, return this error and signout.
    store.dispatch(signOut());
    localStorage.setItem('SP', 'synced-sign-out');
    return Promise.reject(error);
  }
};

export const replayAfterMfa = () =>
  replayHeldRequests((config) => axios({ ...config, withCredentials: true }));

export default instance;
export const useTable = createUseTable(instance);
export type TableInfo = ReturnType<typeof useTable>;

export const useFetch = createUseFetch(instance);

export type ErrorMessage = {
  type: any;
  fieldName: string;
  messageText: string;
};
export interface ApiResponse<T = any, M = any> {
  id: number;
  details: T;
  metadata: M[];
  status: '0' | '1';
  errors: ErrorMessage[];
}
